class SubscribeAndSaveBlock extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.isBusy = false;
    this.variants = this.parseMainVariants();
    this.currentVariantInput = this.querySelector('[data-current-variant-id]');
    this.optionCards = Array.from(this.querySelectorAll('[data-subscribe-and-save-card]'));
    this.purchaseOptionInputs = Array.from(this.querySelectorAll('[data-subscribe-and-save-purchase-option]'));
    this.frequencyToggle = this.querySelector('[data-subscribe-and-save-frequency-toggle]');
    this.frequencyPanel = this.querySelector('[data-subscribe-and-save-frequency-panel]');
    this.frequencyOptions = Array.from(this.querySelectorAll('[data-subscribe-and-save-frequency-option]'));
    this.householdButtons = Array.from(this.querySelectorAll('[data-subscribe-and-save-household-option]'));
    this.recommendedLabel = this.querySelector('[data-subscribe-and-save-recommended-label]');
    this.badge = this.querySelector('[data-subscribe-and-save-badge]');
    this.priceCompare = this.querySelector('[data-subscribe-and-save-compare]');
    this.priceCurrent = this.querySelector('[data-subscribe-and-save-current-price]');
    this.oncePrice = this.querySelector('[data-subscribe-and-save-once-price]');
    this.description = this.querySelector('[data-subscribe-and-save-description]');
    this.summary = this.querySelector('[data-subscribe-and-save-summary]');
    this.summaryToday = this.querySelector('[data-subscribe-and-save-summary-today]');
    this.summaryFutureLabel = this.querySelector('[data-subscribe-and-save-summary-future-label]');
    this.summaryRefill = this.querySelector('[data-subscribe-and-save-summary-refill]');
    this.ctaButton = this.querySelector('[data-subscribe-and-save-cta]');
    this.ctaText = this.querySelector('[data-subscribe-and-save-cta-text]');
    this.errorRegion = this.querySelector('[data-subscribe-and-save-error]');
    this.subscribeOption = this.querySelector('.subscribe-and-save__option--subscribe');
    this.onetimeOption = this.querySelector('.subscribe-and-save__option--onetime');
    this.trustSlider = this.querySelector('[data-subscribe-and-save-trust-slider]');
    this.trustTrack = this.querySelector('[data-subscribe-and-save-trust-track]');
    this.trustSlides = Array.from(this.querySelectorAll('[data-subscribe-and-save-trust-slide]'));
    this.trustDots = Array.from(this.querySelectorAll('[data-subscribe-and-save-trust-dot]'));
    this.trustIndex = 0;
    this.pdpRoot = this.closest('pdp-main-product, pdp-main-product-new-page');
    this.stickyButton = this.pdpRoot?.querySelector('[data-pdp-sticky-add-to-cart]') || null;
    this.controlsStickyButton = Boolean(this.stickyButton && !this.pdpRoot?.querySelector('[data-buy-buttons-block]'));

    this.selectedPlan = this.dataset.initialPlan === 'once' ? 'once' : 'subscription';
    this.selectedHousehold =
      this.householdButtons.find((button) => button.classList.contains('is-active'))?.dataset.householdValue ||
      this.householdButtons[0]?.dataset.householdValue ||
      '';
    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();

    this.onOptionCardClick = this.handleOptionCardClick.bind(this);
    this.onOptionCardKeydown = this.handleOptionCardKeydown.bind(this);
    this.onPurchaseOptionChange = this.handlePurchaseOptionChange.bind(this);
    this.onFrequencyToggleClick = this.handleFrequencyToggleClick.bind(this);
    this.onFrequencyOptionChange = this.handleFrequencyOptionChange.bind(this);
    this.onHouseholdButtonClick = this.handleHouseholdButtonClick.bind(this);
    this.onCtaClick = this.handleCtaClick.bind(this);
    this.onVariantInputChange = this.handleVariantInputChange.bind(this);
    this.onStickyClick = this.handleStickyClick.bind(this);
    this.onTrustDotClick = this.handleTrustDotClick.bind(this);
    this.onTrustPointerEnter = this.stopTrustAutoplay.bind(this);
    this.onTrustPointerLeave = this.startTrustAutoplay.bind(this);

    this.optionCards.forEach((card) => {
      card.addEventListener('click', this.onOptionCardClick);
      card.addEventListener('keydown', this.onOptionCardKeydown);
    });
    this.purchaseOptionInputs.forEach((input) => input.addEventListener('change', this.onPurchaseOptionChange));
    this.frequencyToggle?.addEventListener('click', this.onFrequencyToggleClick);
    this.frequencyOptions.forEach((input) => input.addEventListener('change', this.onFrequencyOptionChange));
    this.householdButtons.forEach((button) => button.addEventListener('click', this.onHouseholdButtonClick));
    this.currentVariantInput?.addEventListener('change', this.onVariantInputChange);
    this.ctaButton?.addEventListener('click', this.onCtaClick);

    if (this.controlsStickyButton) {
      this.stickyButton.addEventListener('click', this.onStickyClick, { capture: true });
    }

    this.setupTrustSlider();
    this.applyHouseholdRecommendation(this.getSelectedHouseholdButton(), { sync: false });
    this.syncUI();
  }

  disconnectedCallback() {
    this.optionCards?.forEach((card) => {
      card.removeEventListener('click', this.onOptionCardClick);
      card.removeEventListener('keydown', this.onOptionCardKeydown);
    });
    this.purchaseOptionInputs?.forEach((input) => input.removeEventListener('change', this.onPurchaseOptionChange));
    this.frequencyToggle?.removeEventListener('click', this.onFrequencyToggleClick);
    this.frequencyOptions?.forEach((input) => input.removeEventListener('change', this.onFrequencyOptionChange));
    this.householdButtons?.forEach((button) => button.removeEventListener('click', this.onHouseholdButtonClick));
    this.currentVariantInput?.removeEventListener('change', this.onVariantInputChange);
    this.ctaButton?.removeEventListener('click', this.onCtaClick);

    if (this.controlsStickyButton && this.stickyButton) {
      this.stickyButton.removeEventListener('click', this.onStickyClick, { capture: true });
    }

    this.teardownTrustSlider();
  }

  setupTrustSlider() {
    if (!(this.trustSlider instanceof HTMLElement) || !(this.trustTrack instanceof HTMLElement)) return;
    if (this.trustSlides.length <= 1) return;

    this.trustDots.forEach((dot) => dot.addEventListener('click', this.onTrustDotClick));
    this.trustSlider.addEventListener('mouseenter', this.onTrustPointerEnter);
    this.trustSlider.addEventListener('mouseleave', this.onTrustPointerLeave);
    this.trustSlider.addEventListener('focusin', this.onTrustPointerEnter);
    this.trustSlider.addEventListener('focusout', this.onTrustPointerLeave);

    this.setTrustSlide(0);
    this.startTrustAutoplay();
  }

  teardownTrustSlider() {
    this.stopTrustAutoplay();
    this.trustDots?.forEach((dot) => dot.removeEventListener('click', this.onTrustDotClick));
    this.trustSlider?.removeEventListener('mouseenter', this.onTrustPointerEnter);
    this.trustSlider?.removeEventListener('mouseleave', this.onTrustPointerLeave);
    this.trustSlider?.removeEventListener('focusin', this.onTrustPointerEnter);
    this.trustSlider?.removeEventListener('focusout', this.onTrustPointerLeave);
  }

  handleTrustDotClick(event) {
    const dot = event.currentTarget;
    if (!(dot instanceof HTMLElement)) return;

    const index = Number.parseInt(dot.dataset.index || '', 10);
    if (Number.isNaN(index)) return;

    this.setTrustSlide(index);
    this.restartTrustAutoplay();
  }

  setTrustSlide(index) {
    if (!(this.trustTrack instanceof HTMLElement) || this.trustSlides.length === 0) return;

    const slideCount = this.trustSlides.length;
    this.trustIndex = ((index % slideCount) + slideCount) % slideCount;
    this.trustTrack.style.transform = `translateX(-${this.trustIndex * 100}%)`;

    this.trustDots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.trustIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  startTrustAutoplay() {
    if (this.trustSlides.length <= 1 || this.trustAutoplayTimer) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    this.trustAutoplayTimer = window.setInterval(() => {
      this.setTrustSlide(this.trustIndex + 1);
    }, 4000);
  }

  stopTrustAutoplay() {
    if (!this.trustAutoplayTimer) return;

    window.clearInterval(this.trustAutoplayTimer);
    this.trustAutoplayTimer = null;
  }

  restartTrustAutoplay() {
    this.stopTrustAutoplay();
    this.startTrustAutoplay();
  }

  parseMainVariants() {
    const script = this.querySelector('[data-subscribe-and-save-main-variants-json]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  getDatasetVariant() {
    const id = Number.parseInt(this.dataset.mainVariantId || '', 10);
    const price = Number.parseInt(this.dataset.mainPriceCents || '0', 10);
    const compareAtPrice = Number.parseInt(this.dataset.mainCompareCents || '0', 10);

    return {
      id,
      available: this.dataset.mainAvailable === 'true',
      price: Number.isNaN(price) ? 0 : price,
      compare_at_price: Number.isNaN(compareAtPrice) ? 0 : compareAtPrice,
    };
  }

  getCurrentVariantId() {
    const inputId = Number.parseInt(this.currentVariantInput?.value || '', 10);
    if (!Number.isNaN(inputId)) return inputId;

    const rootId = Number.parseInt(this.pdpRoot?.currentVariantId || '', 10);
    if (!Number.isNaN(rootId)) return rootId;

    const datasetId = Number.parseInt(this.dataset.mainVariantId || '', 10);
    return Number.isNaN(datasetId) ? null : datasetId;
  }

  getVariantById(variantId) {
    if (!variantId) return null;
    return this.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  handleVariantInputChange() {
    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();
    this.syncUI();
  }

  handleOptionCardClick(event) {
    const card = event.currentTarget;
    if (!(card instanceof HTMLElement)) return;
    if (card.getAttribute('aria-disabled') === 'true') return;

    const interactiveTarget = event.target instanceof HTMLElement
      ? event.target.closest('button, input, select, textarea, a')
      : null;

    if (interactiveTarget && interactiveTarget !== card) return;

    this.selectPlan(card.dataset.subscribeAndSaveCard === 'once' ? 'once' : 'subscription');
  }

  handleOptionCardKeydown(event) {
    if (event.target instanceof HTMLElement && event.target.closest('button, input, select, textarea, a')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.handleOptionCardClick(event);
  }

  handlePurchaseOptionChange(event) {
    const value = event.target?.dataset.subscribeAndSavePurchaseOption;
    this.selectPlan(value === 'subscription' ? 'subscription' : 'once');
  }

  handleFrequencyToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!(this.frequencyPanel instanceof HTMLElement) || !(this.frequencyToggle instanceof HTMLElement)) return;

    const isOpen = !this.frequencyPanel.hidden;

    this.frequencyPanel.hidden = isOpen;
    this.frequencyToggle.setAttribute('aria-expanded', String(!isOpen));
  }

  handleFrequencyOptionChange(event) {
    event.stopPropagation();
    this.selectedPlan = 'subscription';
    this.clearError();
    this.syncHouseholdSelectionToFrequency();
    this.syncUI();
  }

  handleHouseholdButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    this.applyHouseholdRecommendation(button, { sync: false });
    this.selectPlan('subscription');
  }

  handleCtaClick(event) {
    event.preventDefault();
    this.addSelectionToCart({ redirectToCheckout: false });
  }

  handleStickyClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addSelectionToCart({ redirectToCheckout: false });
  }

  selectPlan(plan) {
    if (plan === 'subscription' && !this.hasSubscriptionOptions()) return;

    this.selectedPlan = plan === 'once' ? 'once' : 'subscription';
    this.clearError();
    this.syncUI();
  }

  hasSubscriptionOptions() {
    return this.frequencyOptions.length > 0;
  }

  getSelectedFrequencyOption() {
    return this.frequencyOptions.find((input) => input.checked) || this.frequencyOptions[0] || null;
  }

  getSelectedHouseholdButton() {
    return (
      this.householdButtons.find((button) => button.dataset.householdValue === this.selectedHousehold) ||
      this.householdButtons.find((button) => button.classList.contains('is-active')) ||
      this.householdButtons[0] ||
      null
    );
  }

  normalizeMatch(value) {
    return String(value || '').trim().toLowerCase();
  }

  frequencyOptionMatches(option, rawMatch) {
    const match = this.normalizeMatch(rawMatch);
    if (!match || !(option instanceof HTMLInputElement)) return false;

    const sellingPlanId = this.normalizeMatch(option.dataset.sellingPlanId || option.value);
    const planMonths = this.normalizeMatch(option.dataset.planMonths);
    const frequencyLabel = this.normalizeMatch(option.dataset.frequencyLabel);

    if (sellingPlanId === match || planMonths === match) return true;

    if (/^\d+$/.test(match)) {
      return new RegExp(`(^|\\D)${match}(\\D|$)`).test(frequencyLabel);
    }

    return frequencyLabel.includes(match);
  }

  findFrequencyOptionByMatch(match) {
    return this.frequencyOptions.find((option) => this.frequencyOptionMatches(option, match)) || null;
  }

  applyHouseholdRecommendation(button, { sync = true } = {}) {
    if (!(button instanceof HTMLElement)) return false;

    this.selectedHousehold = button.dataset.householdValue || '';
    this.updateHouseholdButtons();

    const matchingOption = this.findFrequencyOptionByMatch(button.dataset.planMatch);

    if (matchingOption instanceof HTMLInputElement) {
      matchingOption.checked = true;
    }

    if (sync) this.syncUI();
    return matchingOption instanceof HTMLInputElement;
  }

  syncHouseholdSelectionToFrequency() {
    const selectedOption = this.getSelectedFrequencyOption();
    if (!(selectedOption instanceof HTMLInputElement)) return;

    const currentButton = this.getSelectedHouseholdButton();
    if (currentButton instanceof HTMLElement && this.frequencyOptionMatches(selectedOption, currentButton.dataset.planMatch)) {
      this.updateHouseholdButtons();
      return;
    }

    const matchingButton = this.householdButtons.find((button) =>
      this.frequencyOptionMatches(selectedOption, button.dataset.planMatch)
    );

    if (matchingButton instanceof HTMLElement) {
      this.selectedHousehold = matchingButton.dataset.householdValue || '';
    }

    this.updateHouseholdButtons();
  }

  updateHouseholdButtons() {
    this.householdButtons.forEach((button) => {
      const isActive = button.dataset.householdValue === this.selectedHousehold;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  getSelectedSellingPlanId() {
    const option = this.getSelectedFrequencyOption();
    const sellingPlanId = Number.parseInt(option?.dataset.sellingPlanId || option?.value || '', 10);
    return Number.isNaN(sellingPlanId) ? null : sellingPlanId;
  }

  isSubscriptionSelected() {
    return this.selectedPlan === 'subscription';
  }

  hasValidSubscriptionSelection() {
    if (!this.isSubscriptionSelected()) return true;

    return Boolean(this.getSelectedSellingPlanId() && this.getRefillVariantId());
  }

  getMainVariantId() {
    const variantId = Number.parseInt(this.currentVariant?.id || '', 10);
    if (!Number.isNaN(variantId)) return variantId;

    return this.getCurrentVariantId();
  }

  getRefillVariantId() {
    const refillVariantId = Number.parseInt(this.dataset.refillVariantId || '', 10);
    return Number.isNaN(refillVariantId) ? null : refillVariantId;
  }

  getRefillQuantity() {
    const option = this.getSelectedFrequencyOption();
    const optionQuantity = Number.parseInt(option?.dataset.refillQuantity || '', 10);
    if (!Number.isNaN(optionQuantity) && optionQuantity > 0) return optionQuantity;

    const fallbackQuantity = Number.parseInt(this.dataset.refillQuantity || '1', 10);
    return Number.isNaN(fallbackQuantity) ? 1 : Math.max(1, fallbackQuantity);
  }

  getRefillPriceCents() {
    const option = this.getSelectedFrequencyOption();
    const price = Number.parseInt(option?.dataset.refillPriceCents || '0', 10);
    return Number.isNaN(price) ? 0 : price;
  }

  getFrequencyLabel() {
    const option = this.getSelectedFrequencyOption();
    return (option?.dataset.frequencyLabel || '').trim();
  }

  getMainPriceCents() {
    const price = Number(this.currentVariant?.price ?? this.dataset.mainPriceCents ?? 0);
    return Number.isFinite(price) ? price : 0;
  }

  getMainCompareCents() {
    const compareAtPrice = Number(this.currentVariant?.compare_at_price ?? this.dataset.mainCompareCents ?? 0);
    return Number.isFinite(compareAtPrice) ? compareAtPrice : 0;
  }

  getDiscountAmountCents() {
    const discount = Number.parseInt(this.dataset.discountAmountCents || '0', 10);
    return Number.isNaN(discount) ? 0 : Math.max(0, discount);
  }

  getDiscountPercent() {
    const percent = Number(this.dataset.discountPercent || '0');
    if (!Number.isFinite(percent)) return 0;
    return Math.max(0, Math.min(100, percent));
  }

  getSubscriptionFixedPriceCents() {
    const price = Number.parseInt(this.dataset.subscriptionFixedPriceCents || '0', 10);
    return Number.isNaN(price) ? 0 : Math.max(0, price);
  }

  getSubscriptionMainPriceCents() {
    const mainPrice = this.getMainPriceCents();
    let mode = this.dataset.subscriptionPriceMode || 'percent';
    const amountDiscount = this.getDiscountAmountCents();
    const percentDiscount = this.getDiscountPercent();
    const fixedPrice = this.getSubscriptionFixedPriceCents();

    if (mode === 'amount' && amountDiscount <= 0) {
      mode = 'percent';
    } else if (mode === 'fixed' && fixedPrice <= 0) {
      mode = 'percent';
    }

    if (mode === 'fixed') {
      if (fixedPrice > 0) return fixedPrice;
    }

    if (mode === 'percent') {
      const effectivePercent = percentDiscount > 0 ? percentDiscount : 20;
      return Math.max(0, Math.round((mainPrice * (100 - effectivePercent)) / 100));
    }

    return Math.max(0, mainPrice - amountDiscount);
  }

  getTodayPriceCents() {
    return this.isSubscriptionSelected() ? this.getSubscriptionMainPriceCents() : this.getMainPriceCents();
  }

  getDiscountCents() {
    return Math.max(0, this.getMainPriceCents() - this.getSubscriptionMainPriceCents());
  }

  getRefillPercent() {
    const option = this.getSelectedFrequencyOption();
    const percent = Number.parseInt(option?.dataset.refillPercent || '0', 10);
    return Number.isNaN(percent) ? 0 : percent;
  }

  getMainDiscountPercent() {
    const mainPrice = this.getMainPriceCents();
    const discount = this.getDiscountCents();
    if (mainPrice <= 0 || discount <= 0) return 0;

    return Math.round((discount / mainPrice) * 100);
  }

  getDisplayPercent() {
    return this.getMainDiscountPercent();
  }

  fillTemplate(template, replacements) {
    return Object.entries(replacements).reduce((value, [key, replacement]) => {
      return value.split(`{${key}}`).join(replacement ?? '');
    }, template || '');
  }

  getTemplateReplacements() {
    const discount = this.getDiscountCents();
    const displayPercent = this.getDisplayPercent();

    return {
      amount: this.formatMoney(discount),
      percent: String(displayPercent),
      percent_label: `${displayPercent}%`,
      price: this.formatMoney(this.getRefillPriceCents()),
      plan: this.getFrequencyLabel(),
      interval: this.getFrequencyLabel(),
      quantity: String(this.getRefillQuantity()),
      subscription_price: this.formatMoney(this.getSubscriptionMainPriceCents()),
      compare_price: this.formatMoney(this.getMainPriceCents()),
    };
  }

  syncUI() {
    const subscriptionSelected = this.isSubscriptionSelected();
    const mainPrice = this.getMainPriceCents();
    const subscriptionMainPrice = this.getSubscriptionMainPriceCents();
    const discount = this.getDiscountCents();
    const refillPrice = this.getRefillPriceCents();
    const isAvailable = Boolean(this.currentVariant?.available);
    const hasValidSubscription = this.hasValidSubscriptionSelection();
    const disabled = this.isBusy || !isAvailable || !hasValidSubscription;
    const replacements = this.getTemplateReplacements();

    this.optionCards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const isSubscriptionCard = card.dataset.subscribeAndSaveCard === 'subscription';
      const isSelected = isSubscriptionCard === subscriptionSelected;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.subscribeOption?.classList.toggle('is-selected', subscriptionSelected);
    this.onetimeOption?.classList.toggle('is-selected', !subscriptionSelected);

    this.purchaseOptionInputs.forEach((input) => {
      const isSubscribe = input.dataset.subscribeAndSavePurchaseOption === 'subscription';
      input.checked = isSubscribe === subscriptionSelected;
    });

    if (this.summary instanceof HTMLElement) {
      this.summary.hidden = !subscriptionSelected;
    }

    const displayPercent = this.getDisplayPercent();

    if (this.badge instanceof HTMLElement) {
      const badgeTemplate = this.dataset.badgeTemplate || '';
      this.badge.hidden = displayPercent <= 0 || !badgeTemplate;
      if (!this.badge.hidden) {
        this.badge.textContent = this.fillTemplate(badgeTemplate, replacements);
      }
    }

    if (this.description instanceof HTMLElement) {
      this.description.textContent = this.fillTemplate(this.dataset.subscriptionDescriptionTemplate || '', replacements);
    }

    if (this.recommendedLabel instanceof HTMLElement) {
      this.recommendedLabel.textContent = this.fillTemplate(
        this.dataset.recommendedDeliveryTemplate || 'Recommended delivery: {plan}',
        replacements
      );
    }

    if (this.priceCurrent instanceof HTMLElement) {
      this.priceCurrent.textContent = this.formatMoney(subscriptionMainPrice);
    }

    if (this.priceCompare instanceof HTMLElement) {
      const shouldShowCompare = this.dataset.showSubscriptionComparePrice !== 'false' && discount > 0;
      this.priceCompare.hidden = !shouldShowCompare;
      this.priceCompare.textContent = shouldShowCompare ? this.formatMoney(mainPrice) : '';
    }

    if (this.oncePrice instanceof HTMLElement) {
      this.oncePrice.textContent = this.formatMoney(mainPrice);
    }

    if (this.summaryToday instanceof HTMLElement) {
      this.summaryToday.textContent = this.formatMoney(this.getTodayPriceCents());
    }

    if (this.summaryFutureLabel instanceof HTMLElement) {
      this.summaryFutureLabel.textContent = this.fillTemplate(this.dataset.summaryFutureTemplate || '', replacements);
    }

    if (this.summaryRefill instanceof HTMLElement) {
      this.summaryRefill.textContent = this.formatMoney(refillPrice);
    }

    const ctaText = `${this.dataset.ctaLabel || 'Add to cart'} - ${this.formatMoney(this.getTodayPriceCents())}`;

    if (this.ctaText instanceof HTMLElement) {
      this.ctaText.textContent = ctaText;
    }

    if (this.ctaButton instanceof HTMLButtonElement) {
      this.ctaButton.disabled = disabled;
    }

    if (this.controlsStickyButton && this.stickyButton instanceof HTMLButtonElement) {
      this.stickyButton.disabled = disabled;
      this.stickyButton.textContent = ctaText;
    }
  }

  shouldLinkSubscriptionItems() {
    return this.dataset.linkSubscriptionItems !== 'false';
  }

  createLinkedGroupId() {
    if (typeof this.pdpRoot?.createLinkedAddonGroupId === 'function') {
      return this.pdpRoot.createLinkedAddonGroupId();
    }

    if (window.crypto?.randomUUID) return `sas-${window.crypto.randomUUID()}`;
    return `sas-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  buildLinkedProperties({ group, role, removeLinkedRoles = [] }) {
    const properties = {
      _offer_source: `${this.dataset.context || 'pdp'}-subscribe-and-save`,
      _offer_group: group,
      _offer_role: role,
    };

    if (removeLinkedRoles.length > 0) {
      properties._offer_remove_linked_roles = removeLinkedRoles.join(',');
    }

    return properties;
  }

  getSelectedAddons() {
    return typeof this.pdpRoot?.getSelectedAddons === 'function' ? this.pdpRoot.getSelectedAddons() : [];
  }

  buildCartItems() {
    const mainVariantId = this.getMainVariantId();
    const shouldAddSubscriptionRefill = this.isSubscriptionSelected();
    const shouldLinkSubscription =
      shouldAddSubscriptionRefill &&
      this.shouldLinkSubscriptionItems() &&
      Boolean(this.getRefillVariantId() && this.getSelectedSellingPlanId());
    const linkedGroupId = shouldLinkSubscription ? this.createLinkedGroupId() : '';
    const mainRemoveRoles = [];
    const items = [];
    const mainItem = {
      id: mainVariantId,
      quantity: 1,
    };

    if (shouldLinkSubscription) {
      mainRemoveRoles.push('subscription_refill');
    }

    if (linkedGroupId && mainRemoveRoles.length > 0) {
      mainItem.properties = this.buildLinkedProperties({
        group: linkedGroupId,
        role: 'main',
        removeLinkedRoles: mainRemoveRoles,
      });
    }

    items.push(mainItem);

    if (shouldAddSubscriptionRefill) {
      const refillVariantId = this.getRefillVariantId();
      const sellingPlanId = this.getSelectedSellingPlanId();

      if (refillVariantId && sellingPlanId) {
        const refillProperties = {
          _subscribe_and_save_frequency: this.getFrequencyLabel(),
        };

        if (shouldLinkSubscription && linkedGroupId) {
          Object.assign(
            refillProperties,
            this.buildLinkedProperties({
              group: linkedGroupId,
              role: 'subscription_refill',
              removeLinkedRoles: ['main'],
            })
          );
        }

        items.push({
          id: refillVariantId,
          quantity: this.getRefillQuantity(),
          selling_plan: sellingPlanId,
          properties: refillProperties,
        });
      }
    }

    return items;
  }

  async addSelectionToCart({ redirectToCheckout = false } = {}) {
    if (this.isBusy) return;

    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();

    if (!this.currentVariant?.id || !this.currentVariant.available) {
      this.showError(this.dataset.unavailableText || 'This product is currently unavailable.');
      this.syncUI();
      return;
    }

    if (!this.hasValidSubscriptionSelection()) {
      this.showError(this.dataset.subscriptionUnavailableText || 'Select a valid subscription plan.');
      this.syncUI();
      return;
    }

    this.clearError();
    this.isBusy = true;
    this.syncUI();

    const items = this.buildCartItems();
    const discountCode = (this.dataset.discountCode || '').trim();
    const isSubscription = this.isSubscriptionSelected();
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((item) => item.getAttribute('data-section-id') || '')
      .filter(Boolean);
    const requestBody = { items };

    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || this.dataset.unavailableText || 'Unable to add to cart.');
      }

      let cartUpdateResource = data;

      if (isSubscription && discountCode) {
        await this.applyDiscountCode(discountCode);
        cartUpdateResource = { ...data, sections: {} };
      }

      const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      this.dispatchCartUpdate(cartUpdateResource, totalQuantity);

      if (redirectToCheckout) {
        window.location.href = this.getCheckoutUrl(isSubscription ? discountCode : '');
      } else {
        this.openCartDrawer();
      }
    } catch (error) {
      console.error(error);
      this.showError(error instanceof Error ? error.message : this.dataset.unavailableText || 'Unable to add to cart.');
    } finally {
      this.isBusy = false;
      this.syncUI();
    }
  }

  async applyDiscountCode(code) {
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
    } catch (error) {
      console.warn(error);
    }
  }

  dispatchCartUpdate(resource, quantity) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource,
          sourceId: this.id,
          data: {
            source: this.dataset.context || 'subscribe-and-save',
            itemCount: quantity,
            variantId: String(this.currentVariant?.id || ''),
            sections: resource.sections || {},
          },
        },
      })
    );
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!(cartDrawer instanceof HTMLElement)) return;

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    if (typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog();
    }
  }

  getCartAddUrl() {
    return this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add.js';
  }

  getCheckoutUrl(discountCode = '') {
    const checkoutUrl = this.dataset.buyNowUrl || '/checkout';
    if (!discountCode) return checkoutUrl;

    const separator = checkoutUrl.includes('?') ? '&' : '?';
    return `${checkoutUrl}${separator}discount=${encodeURIComponent(discountCode)}`;
  }

  formatMoney(cents) {
    const normalizedCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const currency = this.dataset.currencyCode || 'EUR';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: this.dataset.showCurrencyCode === 'true' ? 'code' : 'symbol',
      }).format(normalizedCents / 100);
    } catch (error) {
      return `${(normalizedCents / 100).toFixed(2)} ${currency}`.trim();
    }
  }

  showError(message) {
    if (!(this.errorRegion instanceof HTMLElement)) return;

    this.errorRegion.textContent = message;
    this.errorRegion.hidden = false;
  }

  clearError() {
    if (!(this.errorRegion instanceof HTMLElement)) return;

    this.errorRegion.textContent = '';
    this.errorRegion.hidden = true;
  }
}

if (!customElements.get('subscribe-and-save-block')) {
  customElements.define('subscribe-and-save-block', SubscribeAndSaveBlock);
}
